import { getUniqueId, EventEmitterWrapper } from '@umu/umu-util';
import './index.scss';
import { reportError } from 'common/src/services/errorLogger';
import { isWeChat } from '@umu/umu-ua';

let manager: PlayerManager | null = null;

const isHlsUrl = (url?: string): boolean => {
    if (!url) return false;
    try {
        return new URL(url).pathname.endsWith('.m3u8');
    } catch {
        return url.endsWith('.m3u8');
    }
};

const MAX_HLS_FATAL_RETRY = 3; // 快速重试阶段：致命错误的最大重试次数（指数退避）
const HLS_RETRY_BASE_DELAY = 1000; // 快速重试基础延时（ms），随重试次数指数递增
const HLS_SLOW_RETRY_INTERVAL = 20000; // 快速重试预算耗尽后，进入慢速兜底重试的间隔（ms），持续到恢复或播放器销毁为止
// hls.js 默认 30s（1.1.6/1.5.15 两个线上版本均确认为此值）。客户 HAR 显示低码率(0.8Mbps)长分片(~12s)
const HLS_MAX_BUFFER_LENGTH = 60;
const HLS_FRAG_LOAD_POLICY = {
    default: {
        maxTimeToFirstByteMs: 10000,
        maxLoadTimeMs: 120000,
        timeoutRetry: { maxNumRetry: 4, retryDelayMs: 1000, maxRetryDelayMs: 8000 },
        errorRetry: { maxNumRetry: 6, retryDelayMs: 1000, maxRetryDelayMs: 8000 },
    },
};

interface IHlsRecoveryState {
    networkRetryCount: number;
    mediaRetryCount: number;
    lastFatalType: string | null; // 最近一次致命错误的类型，供慢速重试/手动重试判断恢复方式
    isStalled: boolean; // 快速重试预算已耗尽，仅等待慢速兜底或手动触发
    hasReportedStall: boolean; // 避免同一次中断反复上报（慢速重试期间可能多次耗尽）
    slowRetryTimer: ReturnType<typeof setTimeout> | null;
    onOnline: () => void;
}

export type TMediaType = 'video' | 'audio';

export interface IMediaOptions {
    isPrimary?: boolean; // 是否页面中的主播放器，与其它效果不同的时，可以被恢复播放
    autoplay?: boolean;
    controls?: boolean;
    loop?: boolean;
    file: string; // 播放文件路径
    volume?: number; // 音量改成100分制
    lastPlayedTime?: number;
    fileId?: string;
}

// http://www.w3school.com.cn/html5/html5_ref_audio_video_dom.asp
export enum EMediaEvent {
    playing = 'playing',
    canplay = 'canplay',
    waiting = 'waiting',
    progress = 'progress',
    stalled = 'stalled',
    loadstart = 'loadstart',
    timeupdate = 'timeupdate',
    ratechange = 'ratechange',
    pause = 'pause',
    ended = 'ended',
    error = 'error',
    seeking = 'seeking',
    touchstart = 'touchstart',
    touchend = 'touchend',
    pointerdown = 'pointerdown',
    loadedmetadata = 'loadedmetadata',
}

export type TMediaEvent =
    | 'playing'
    | 'canplay'
    | 'waiting'
    | 'progress'
    | 'stalled'
    | 'loadstart'
    | 'timeupdate'
    | 'seeking'
    | 'ratechange'
    | 'pause'
    | 'ended'
    | 'error'
    | 'loadedmetadata';

interface IMediaConfig {
    uniqueId: string;
    type: TMediaType;
    options: IMediaOptions;
    isPrimary: boolean;
    isLoaded: boolean;
    isPlaying: boolean;
    lastPlayedTime: number;
}
const MediaList: { [key: string]: IMediaConfig } = {};

// 获取视频加载状态
const getVideoElementState = (videoElement: HTMLVideoElement) => {
    return {
        src: videoElement?.src,
        // 当前缓冲范围，返回 TimeRanges 对象
        buffered: videoElement?.buffered,
        // 视频是否已经加载完成
        readyState: videoElement?.readyState,
        // 当前网络状态
        networkState: videoElement?.networkState,
        // 是否正在加载数据
        seeking: videoElement?.seeking,
        // 视频是否已加载足够数据可以开始播放
        // 0 没有关于音频/视频是否就绪的信息
        // 1 已获取元数据
        // 2 当前播放位置的数据是可用的，但没有足够的数据来播放下一帧/毫秒
        // 3 当前及至少下一帧的数据是可用的
        // 4 可用数据足够开始播放
        canPlay: videoElement?.readyState >= 3,
        ended: videoElement?.ended,
        paused: videoElement?.paused,
        volume: videoElement?.volume,
        currentTime: videoElement?.currentTime,
        duration: videoElement?.duration,
    };
};

class PlayerManager extends EventEmitterWrapper {
    private isVideoCanPlay: boolean = false;
    private isPendingPlay: boolean = false;
    public static getInstance() {
        if (!manager) {
            manager = new PlayerManager();
        }
        return manager;
    }

    private playingUniqueId: string = '';
    // private audioPlayer: HTMLAudioElement | null = null;
    private videoPlayerMap: { [key: string]: HTMLVideoElement } = {};
    private hlsMap: Map<string, any> = new Map();
    private hlsRecoveryMap: Map<string, IHlsRecoveryState> = new Map();

    public getPlayer(type: TMediaType, uniqueId: string) {
        if (type === 'video') {
            if (!this.videoPlayerMap[uniqueId]) {
                this.videoPlayerMap[uniqueId] = document.createElement('video');
                this.initPlayer(this.videoPlayerMap[uniqueId], type);
                this.bindEvent(this.videoPlayerMap[uniqueId], uniqueId);
            }
            return this.videoPlayerMap[uniqueId];
        }

        // 目前没有看到有用到 audio 的地方，先注释掉
        // if (type === 'audio') {
        //     if (!this.audioPlayer) {
        //         this.audioPlayer = document.createElement('audio');
        //         this.audioPlayer.className = 'audio-player-el';
        //         this.bindEvent(this.audioPlayer);
        //     }
        //     return this.audioPlayer;
        // }
        reportError({
            error: 'Player type error, unable to get instance',
            componentName: 'PlayerManager',
            additionalInfo: { type },
        });
        return null;
    }

    public add(type: TMediaType, options: IMediaOptions) {
        const uniqueId = getUniqueId(type + '-');
        MediaList[uniqueId] = {
            uniqueId,
            type,
            options,
            isPrimary: !!options.isPrimary,
            isLoaded: false,
            isPlaying: false,
            lastPlayedTime: options.lastPlayedTime || 0,
        };
        return uniqueId;
    }

    public getCurrentMediaConfig() {
        const uniqueId = this.playingUniqueId;
        const config = MediaList[uniqueId];
        return config;
    }

    private initPlayerOptions(config: IMediaConfig, uniqueId: string) {
        const player = this.getPlayer(config.type, uniqueId);
        if (!player) {
            return;
        }

        const options = config.options;

        player.autoplay = !!options.autoplay;
        player.controls = !!options.controls;
        player.loop = !!options.loop;
        player.volume = (options.volume || 0) / 100;

        // 音频重复设置没问题，视频重复设置会闪一次，因此控制一下
        // CN TCPPlayer 加密不走这套逻辑，fileId 有值是老逻辑判断是否 TCPPlayer 加密资源的标准
        if (!options.fileId) {
            // CN 目前没有开放非加密白名单的 HLS，控制范围，不走
            if (isHlsUrl(options.file)) {
                this.attachHls(player, options.file, uniqueId);
            } else if (player.src !== options.file) {
                player.src = options.file;
            }
        }
    }

    private attachHls(player: HTMLVideoElement, url: string, uniqueId: string) {
        if (this.hlsMap.has(uniqueId)) {
            return;
        }

        // 保证所有走这条路径的播放器都有同一套重试/缓冲区逻辑兜底，避免出现两套不一致的行为
        const HlsClass = (window as any).Hls;
        if (HlsClass?.isSupported()) {
            const hls = new HlsClass({
                maxBufferLength: HLS_MAX_BUFFER_LENGTH,
                fragLoadPolicy: HLS_FRAG_LOAD_POLICY,
            });
            hls.loadSource(url);
            hls.attachMedia(player);
            this.hlsMap.set(uniqueId, hls);
            this.bindHlsErrorRecovery(HlsClass, hls, uniqueId);
            return;
        }
        // hls.js not loaded (CDN failure) or MSE not supported — fall back to direct src assignment.
        // Browsers with native HLS may still play it; others fire an error event so onError can handle it.
        reportError({
            error: !HlsClass ? 'hls.js not loaded — window.Hls is undefined' : 'HLS not supported (MSE unavailable)',
            componentName: 'PlayerManager',
            additionalInfo: { url, uniqueId },
        });
        player.src = url;
    }

    private bindHlsErrorRecovery(HlsClass: any, hls: any, uniqueId: string) {
        const state: IHlsRecoveryState = {
            networkRetryCount: 0,
            mediaRetryCount: 0,
            lastFatalType: null,
            isStalled: false,
            hasReportedStall: false,
            slowRetryTimer: null,
            onOnline: () => {
                // 网络恢复是最强的恢复信号，无论当前处于快速重试还是慢速兜底阶段都直接重置并重试
                state.networkRetryCount = 0;
                state.isStalled = false;
                this.clearSlowRetry(state);
                hls.startLoad();
            },
        };
        this.hlsRecoveryMap.set(uniqueId, state);
        window.addEventListener('online', state.onOnline);

        hls.on(HlsClass.Events.FRAG_LOADED, () => {
            state.networkRetryCount = 0;
            state.mediaRetryCount = 0;
            state.isStalled = false;
            state.hasReportedStall = false;
            this.clearSlowRetry(state);
        });

        hls.on(HlsClass.Events.ERROR, (_event: string, data: any) => {
            if (!data?.fatal) {
                return;
            }
            state.lastFatalType = data.type;
            switch (data.type) {
                case HlsClass.ErrorTypes.NETWORK_ERROR:
                    if (state.networkRetryCount < MAX_HLS_FATAL_RETRY) {
                        const delay = HLS_RETRY_BASE_DELAY * 2 ** state.networkRetryCount;
                        state.networkRetryCount++;
                        setTimeout(() => {
                            // 重试前确认该 hls 实例仍在使用（未被销毁/替换）
                            if (this.hlsMap.get(uniqueId) === hls) {
                                hls.startLoad();
                            }
                        }, delay);
                    } else {
                        this.reportStallOnce(
                            state,
                            uniqueId,
                            'HLS fatal network error, falling back to slow retry',
                            data,
                        );
                        this.scheduleSlowRetry(uniqueId);
                    }
                    break;
                case HlsClass.ErrorTypes.MEDIA_ERROR:
                    // recoverMediaError() 对持续性错误重复调用可能死循环，快速重试阶段限制次数，
                    // 耗尽后交给慢速兜底/手动重试，而不是直接销毁播放器
                    if (state.mediaRetryCount < MAX_HLS_FATAL_RETRY) {
                        state.mediaRetryCount++;
                        hls.recoverMediaError();
                    } else {
                        this.reportStallOnce(
                            state,
                            uniqueId,
                            'HLS fatal media error, falling back to slow retry',
                            data,
                        );
                        this.scheduleSlowRetry(uniqueId);
                    }
                    break;
                default:
                    this.reportStallOnce(
                        state,
                        uniqueId,
                        'HLS unrecoverable fatal error, falling back to slow retry',
                        data,
                    );
                    this.scheduleSlowRetry(uniqueId);
                    break;
            }
        });
    }

    // 同一次中断可能反复耗尽快速重试预算，只在进入耗尽状态的第一次上报，避免长时间弱网下刷屏式上报
    private reportStallOnce(state: IHlsRecoveryState, uniqueId: string, error: string, data: any) {
        if (state.hasReportedStall) {
            return;
        }
        state.hasReportedStall = true;
        reportError({
            error,
            componentName: 'PlayerManager',
            additionalInfo: { uniqueId, errorType: data?.type, errorDetails: data?.details },
        });
    }

    // 快速重试耗尽后不再放弃：以较低频率持续尝试，直到恢复或播放器被销毁，
    // 避免持续性弱网（非彻底断网，不会触发 online 事件）导致视频永久卡死
    private scheduleSlowRetry(uniqueId: string) {
        const state = this.hlsRecoveryMap.get(uniqueId);
        if (!state || state.slowRetryTimer) {
            return;
        }
        state.isStalled = true;
        state.slowRetryTimer = setTimeout(() => {
            state.slowRetryTimer = null;
            if (!this.hlsMap.has(uniqueId)) {
                return;
            }
            this.attemptHlsRecovery(uniqueId);
        }, HLS_SLOW_RETRY_INTERVAL);
    }

    // 统一的恢复动作：由慢速兜底定时器和「点击播放触发的手动重试」共用，
    // 按最近一次致命错误的类型选择恢复方式，避免对同一实例做冲突的重复操作
    private attemptHlsRecovery(uniqueId: string) {
        const hls = this.hlsMap.get(uniqueId);
        const state = this.hlsRecoveryMap.get(uniqueId);
        const HlsClass = (window as any).Hls;
        if (!hls || hls === 'native' || !state || !HlsClass) {
            return;
        }
        this.clearSlowRetry(state);
        state.isStalled = false;
        switch (state.lastFatalType) {
            case HlsClass.ErrorTypes.MEDIA_ERROR:
                state.mediaRetryCount = 0;
                hls.recoverMediaError();
                break;
            case HlsClass.ErrorTypes.NETWORK_ERROR:
                state.networkRetryCount = 0;
                hls.startLoad();
                break;
            default:
                // 未知的致命错误类型，同一实例可能已处于不可预期状态，整例重建更可靠
                this.reattachHls(uniqueId);
                break;
        }
    }

    // 点击播放按钮时的手动兜底：仅在自动重试已耗尽等待时才介入，避免打断正在进行的快速重试
    public retryStalledHls(uniqueId: string) {
        const state = this.hlsRecoveryMap.get(uniqueId);
        if (!state || !state.isStalled) {
            return;
        }
        this.attemptHlsRecovery(uniqueId);
    }

    private clearSlowRetry(state: IHlsRecoveryState) {
        if (state.slowRetryTimer) {
            clearTimeout(state.slowRetryTimer);
            state.slowRetryTimer = null;
        }
    }

    // 用于兜底重建 hls 实例：保留播放位置和播放状态，避免重建后视频回到开头或停在暂停态
    private reattachHls(uniqueId: string) {
        const config = MediaList[uniqueId];
        const player = this.videoPlayerMap[uniqueId];
        if (!config || !player) {
            return;
        }
        const url = config.options.file;
        const resumeTime = player.currentTime;
        const wasPlaying = !player.paused;

        this.destroyHls(uniqueId);
        this.attachHls(player, url, uniqueId);

        const hls = this.hlsMap.get(uniqueId);
        if (!hls || hls === 'native' || !Number.isFinite(resumeTime) || resumeTime <= 0) {
            return;
        }
        const HlsClass = (window as any).Hls;
        const onManifestParsed = () => {
            hls.off(HlsClass.Events.MANIFEST_PARSED, onManifestParsed);
            player.currentTime = resumeTime;
            if (wasPlaying) {
                player.play().catch(() => {
                    // 自动恢复播放失败（如浏览器自动播放策略拦截）时，静默忽略，等待用户手动点击播放
                });
            }
        };
        hls.on(HlsClass.Events.MANIFEST_PARSED, onManifestParsed);
    }

    public destroyHls(uniqueId: string) {
        const hls = this.hlsMap.get(uniqueId);
        if (hls && hls !== 'native') {
            hls.destroy();
        }
        this.hlsMap.delete(uniqueId);
        const recovery = this.hlsRecoveryMap.get(uniqueId);
        if (recovery) {
            window.removeEventListener('online', recovery.onOnline);
            this.clearSlowRetry(recovery);
            this.hlsRecoveryMap.delete(uniqueId);
        }
    }

    public hasHls(uniqueId: string): boolean {
        return this.hlsMap.has(uniqueId);
    }

    public bindPlayerWithUniqueId(player: HTMLVideoElement, uniqueId: string) {
        this.videoPlayerMap[uniqueId] = player;
    }

    public load(uniqueId: string) {
        const config = MediaList[uniqueId];

        // 只有页面中的主播放器才可预加载
        if (!config.isPrimary) {
            return;
        }

        if (config.options.fileId) {
            this.playingUniqueId = uniqueId;
            return;
        }
        const player = this.getPlayer(config.type, uniqueId);

        if (player) {
            if (!config.isLoaded) {
                this.initPlayerOptions(config, uniqueId);
                config.isLoaded = true;
            }
        }
        this.playingUniqueId = uniqueId;

        if (isWeChat) {
            this.isVideoCanPlay = true;
        }

        return player;
    }

    public play(uniqueId: string) {
        this.stopOtherPlayer(uniqueId);
        this.playNext(uniqueId);
    }

    public async playNext(uniqueId: string) {
        const oldConfig = this.getCurrentMediaConfig();

        this.playingUniqueId = uniqueId;
        const config = this.getCurrentMediaConfig();

        if (!config) {
            return;
        }

        if (!oldConfig || config.uniqueId !== oldConfig.uniqueId) {
            this.initPlayerOptions(config, uniqueId);
        }

        const player = this.getPlayer(config.type, uniqueId);
        if (!player) {
            return;
        }
        if (this.isVideoCanPlay) {
            try {
                await player.play();
                config.isPlaying = true;
            } catch (error: any) {
                reportError({
                    error,
                    componentName: 'PlayerManager',
                    additionalInfo: {
                        desc: 'player play failed',
                        uniqueId,
                        config: JSON.stringify(config),
                        playerState: JSON.stringify(getVideoElementState(player)),
                    },
                });
            }
        } else {
            this.isPendingPlay = true;
        }
    }

    private async autoPlayIfNeeded(uniqueId: string) {
        if (!this.isPendingPlay) {
            return;
        }

        const config = this.getCurrentMediaConfig();

        if (!config) {
            return;
        }

        this.isPendingPlay = false;

        const player = this.getPlayer(config.type, uniqueId);
        if (!player) {
            return;
        }

        try {
            await player.play();
        } catch (error) {
            console.error('[PlayerManager]:[autoPlayIfNeeded] play error:', error);
        }
        config.isPlaying = true;
        // player.currentTime = config.isPrimary ? config.lastPlayedTime : 0;
    }

    public stopOtherPlayer(nextId: string) {
        const oldConfig = this.getCurrentMediaConfig();
        if (!oldConfig) {
            return;
        }

        if (oldConfig.uniqueId !== nextId) {
            this.pause(oldConfig.uniqueId, true);
            this.destroyHls(oldConfig.uniqueId);
            const player = this.getPlayer(oldConfig.type, oldConfig.uniqueId);
            if (player) {
                if (oldConfig.isPrimary) {
                    oldConfig.lastPlayedTime = player.currentTime;
                } else {
                    player.currentTime = 0;
                }
            }
        }
    }

    public pause(uniqueId: string, isInterrupted?: boolean) {
        const config = MediaList[uniqueId];
        const player = this.getPlayer(config.type, uniqueId);
        if (!player || player.paused) {
            return;
        }
        config.lastPlayedTime = player.currentTime;
        if (!isInterrupted) {
            config.isPlaying = false;
        }
        player.pause();
    }

    public resetPrimaryPlayer() {
        /* 找到主播放器，如果它是在被播放过程中暂停的，就恢复播放 */
        for (const uniqueId in MediaList) {
            if (MediaList.hasOwnProperty(uniqueId)) {
                const config = MediaList[uniqueId];
                if (config.isPrimary && config.isPlaying) {
                    this.play(uniqueId);
                }
            }
        }
    }

    public initPlayer(player: HTMLAudioElement | HTMLVideoElement, type: TMediaType) {
        if (type === 'video') {
            player.setAttribute('preload', 'metadata');
            player.setAttribute('fetchpriority', 'high');
            player.setAttribute('playsinline', 'true');
            player.setAttribute('webkit-playsinline', 'true');
            player.setAttribute('controlsList', 'nodownload noplaybackrate');
            player.setAttribute('x5-video-player-type', 'h5');
            player.setAttribute('x5-video-player-fullscreen', 'true');
            player.setAttribute('x5-video-orientation', 'landscape|portrait');
        }
    }

    public removeAttribute = (player: HTMLAudioElement | HTMLVideoElement, attributeName: string) => {
        player.removeAttribute(attributeName);
    };

    public bindEvent(player: HTMLAudioElement | HTMLVideoElement, uniqueId: string) {
        for (const event in EMediaEvent) {
            if (!EMediaEvent.hasOwnProperty(event)) {
                return;
            }
            player.addEventListener(event, (...args: any[]) => {
                this.emit(event + '-' + this.playingUniqueId, args);
                if (event === EMediaEvent.ended) {
                    this.pause(this.playingUniqueId);
                    this.resetPrimaryPlayer();
                } else if (event === EMediaEvent.canplay || event === EMediaEvent.loadedmetadata) {
                    this.isVideoCanPlay = true;
                    this.autoPlayIfNeeded(uniqueId);
                }
            });
        }
    }
}

export default PlayerManager.getInstance();
